import { LlmUtils } from '@cotera/utilities';
import type { ContentNode, DocumentState } from '../document-types';
import type { NodeHandler, RenderContext } from './types';
import type { DocumentHandlerContext } from '../document-handler.type';
import { EditorState } from '../editor';
import {
  formatWorkspaceInputMentionMarkdown,
  tryRenderWorkspaceMention,
} from '../workspace-mention-branch';

export class MentionHandler implements NodeHandler {
  readonly type = 'mention';
  // Document mentions in expression mode are wrapped in {{ }}, so we need
  // escapeBackticks = false to prevent the {{ }} from being escaped
  readonly escapeBackticks = false;

  render(
    node: ContentNode | DocumentState,
    _parent: ContentNode | DocumentState | null,
    _childContent: string,
    ctx: RenderContext
  ): string {
    const attrs = (node as ContentNode).attrs;

    // Get the display text (label or id)
    const text = attrs?.['label'] ?? attrs?.['id'] ?? 'unknown';

    // Parse data attribute if it contains type information
    let mentionType: string | undefined;
    if (attrs?.['data']) {
      try {
        const data =
          typeof attrs['data'] === 'string'
            ? JSON.parse(attrs['data'])
            : attrs['data'];
        // Handle both "t" (actual structure) and "type" (legacy)
        mentionType = data?.t ?? data?.type;
      } catch {
        // If parsing fails, data might be a plain string type indicator
        mentionType = attrs['data'];
      }
    }

    if (mentionType === 'tool') {
      if (!attrs?.['label']) {
        return '';
      }
      return `\\\`${LlmUtils.formatToolName(text)}\\\` (tool)`;
    }

    if (ctx.workspace !== undefined) {
      const workspaceRendered = tryRenderWorkspaceMention(
        node as ContentNode,
        text,
        mentionType,
        attrs as Record<string, unknown> | undefined,
        ctx
      );
      if (workspaceRendered !== null) {
        return workspaceRendered;
      }
    }

    if (mentionType === 'input') {
      const inline = formatWorkspaceInputMentionMarkdown(attrs);
      if (inline !== null) {
        return inline;
      }
      return `@${text}`;
    }

    if (mentionType === 'resource') {
      let resourceType: string | undefined;
      let resourceId: string | undefined;
      if (attrs?.['data']) {
        try {
          const parsed =
            typeof attrs['data'] === 'string'
              ? JSON.parse(attrs['data'])
              : attrs['data'];
          const inner = parsed?.data;
          if (
            typeof inner === 'object' &&
            inner !== null &&
            'resourceType' in inner &&
            'resourceId' in inner
          ) {
            resourceType = String(inner['resourceType']);
            resourceId = String(inner['resourceId']);
          }
        } catch {
          // fall through to @text
        }
      }
      if (resourceType !== undefined && resourceId !== undefined) {
        return `@${text} (${resourceType}: ${resourceId})`;
      }
      return `@${text}`;
    }

    if (mentionType === 'column') {
      if (ctx.mode === 'expression') {
        return `{{ "${text}" }}`;
      }
      // Column mentions reference another column in the same relation
      // Format as a column reference
      return `@${text}`;
    }

    if (mentionType === 'document') {
      // Look up the document in the graph
      const builtNodes = ctx.graph.builtNodes();
      const documentNode = builtNodes[text];

      if (documentNode && documentNode.item.t === 'document') {
        // Create a new EditorState from the document's source
        const docEditorState = new EditorState(
          documentNode.item.documentNode.source,
          ctx.handlers.getAllNodeHandlers(),
          ctx.handlers.getAllMarkHandlers(),
          ctx.handlers.getAllGroupHandlersFlat(),
          ctx.messageHandlers
        );

        // Compile the document's markdown
        // Note: The buildCommits process ensures circular references cannot exist,
        // so this recursive compilation is guaranteed to terminate
        const compiledMarkdown = docEditorState.toPromptMarkdown(
          ctx.graph,
          ctx.mode
        );

        if (ctx.mode === 'expression') {
          // In expression mode, wrap the compiled document in {{ }} so it's
          // treated as an expression. The nested document is already wrapped in
          // format_nullable`...` by toPromptMarkdown, so we get:
          //   {{ format_nullable`nested content with {{ expressions }}`  }}
          // This is valid EraQL and allows expressions inside the nested document
          // to be properly evaluated.
          return `<document name="${text}">\n{{ ${compiledMarkdown} }}\n</document>`;
        }

        // In simple mode, just return the compiled document wrapped in XML tags
        return `<document name="${text}">\n${compiledMarkdown}\n</document>`;
      }
    }

    // Default mention format
    return `@${text}`;
  }

  getDependencies(node: ContentNode, _ctx: DocumentHandlerContext): string[] {
    const attrs = node.attrs;
    const id = attrs?.['id'];

    // Check if this is a tool mention - tools are resolved via toolResolver,
    // not via the symbol table, so they shouldn't be treated as dependencies
    if (attrs?.['data']) {
      try {
        const data =
          typeof attrs['data'] === 'string'
            ? JSON.parse(attrs['data'])
            : attrs['data'];
        if (
          data?.t === 'tool' ||
          data?.t === 'resource' ||
          data?.t === 'input'
        ) {
          return []; // Exclude tool / workspace resource / Coco input mentions from graph dependencies
        }
      } catch {
        // Ignore parse errors, treat as regular mention
      }
    }

    if (id && typeof id === 'string') {
      return [id];
    }
    return [];
  }

  replaceReference(
    node: ContentNode,
    oldReference: string,
    newReference: string
  ): ContentNode | null {
    const attrs = node.attrs;
    const id = attrs?.['id'];
    const dataAttr = attrs?.['data'];

    let needsUpdate = false;
    const newAttrs = { ...attrs };

    // Replace the mention ID if it matches
    if (id === oldReference) {
      const label = attrs?.['label'];
      // If label matches the id, update it too
      const newLabel = label === oldReference ? newReference : label;

      newAttrs['id'] = newReference;
      newAttrs['label'] = newLabel;
      needsUpdate = true;
    }

    // Also check if the data attribute contains a reference to the mention
    if (dataAttr) {
      try {
        const data =
          typeof dataAttr === 'string' ? JSON.parse(dataAttr) : dataAttr;

        // Check if data has a 'name' field that matches the mention
        if (
          typeof data === 'object' &&
          data !== null &&
          data['name'] === oldReference
        ) {
          const updatedData = {
            ...data,
            name: newReference,
          };
          newAttrs['data'] = JSON.stringify(updatedData);
          needsUpdate = true;
        }
      } catch {
        // If parsing fails, leave data as-is
      }
    }

    if (needsUpdate) {
      return {
        ...node,
        attrs: newAttrs,
      };
    }

    return null;
  }
}
